import {Form, Input, InputNumber, Select, Switch} from 'antd';
import styles from './instrumentInventory.module.scss';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {Button} from '@dabafinance/react-components';
import {
  CREATE_INVENTORY_MOVEMENT,
  UPDATE_INSTRUMENT_INVENTORY,
} from 'graphql/mutations/assetManagement';
import {v4 as uuid} from 'uuid';
import {GET_ALL_BROKERS} from 'graphql/queries/stocks';
import {useQuery, useMutation} from '@apollo/client';
import {toast} from 'react-toastify';
import {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {defaultInstrumentKey} from 'pages/AssetManagement/modules/instrumentKeys';

export const InstrumentInventory = ({
  onClose,
  refetch,
  form,
  type,
  currency,
  open,
  inventory,
  isTradingEnabled,
}) => {
  const enabledForTrading = Form.useWatch('enabledForTrading', form);

  const [configureInventoryPosition, {loading: updateLoading}] = useMutation(
    UPDATE_INSTRUMENT_INVENTORY,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  useEffect(() => {
    if (inventory) {
      const {averageUnitCost, availableUnits, ...rest} = inventory;
      form.setFieldsValue({
        ...rest,
        units: availableUnits,
        pricePerUnit: averageUnitCost,
      });
    }
  }, [inventory, open]);

  const handleUpdate = values => {
    configureInventoryPosition({
      variables: {
        configureInventoryPositionId: inventory?.id,
        input: {
          ...values,
        },
      },
    })
      .then(({data: {configureInventoryPosition}}) => {
        if (configureInventoryPosition.__typename === 'Error') {
          toast.error(configureInventoryPosition.message);
        } else {
          toast.success(configureInventoryPosition.message);
          onClose();
          refetch();
        }
      })
      .catch(error => toast.error(error.message));
  };

  return (
    <div className={styles.instrumentInventory}>
      <h2>{type === 'enable' ? 'Configure' : ''} Instrument Inventory</h2>

      {isTradingEnabled || type === 'enable' ? (
        <div className={styles.disabled}>
          <p>
            This instrument is currently{' '}
            {isTradingEnabled ? 'enabled' : 'disabled'} for trading.
          </p>
          <Form layout="vertical" form={form} onFinish={handleUpdate}>
            <Form.Item
              name="autoBuyThresholdAmount"
              tooltip="Maximum  sell orders value that can be auto-filled without manual approval"
              label="Auto-buy threshold"
              extra="Tops the position back up when an incoming sell order takes it below the low unit threshold.">
              <InputNumber
                parser={value => value.replace(/\$\s?|(,*)/g, '')}
                min={0}
                className="input-number"
                addonAfter={currency}
              />
            </Form.Item>
            <Form.Item
              tooltip="Minimum inventory level that triggers low-stock warnings"
              name="lowUnitThreshold"
              label="Low Unit Threshold">
              <InputNumber
                parser={value => value.replace(/\$\s?|(,*)/g, '')}
                min={0}
                className="input-number"
                addonAfter="UNITS"
              />
            </Form.Item>
            <Form.Item name="enabledForTrading" label="Enabled For Trading">
              <Switch
                checked={enabledForTrading}
                onChange={e => {
                  form.setFieldsValue({enabledForTrading: e});
                }}
              />
            </Form.Item>
            <SubmitButton disabled={updateLoading} label="Update" />
          </Form>
        </div>
      ) : (
        <div className={styles.inventoryInfo}>
          <h3>Current Inventory Status</h3>
          <div className={styles.infoGrid}>
            <div className={styles.infoItem}>
              <label>Available Units:</label>
              <span>{inventory?.availableUnits || '0.00'}</span>
            </div>
            <div className={styles.infoItem}>
              <label>Average Unit Cost:</label>
              <span>{inventory?.averageUnitCost?.toFixed(2) || '0.00'}</span>
            </div>
            <div className={styles.infoItem}>
              <label>Low Unit Threshold:</label>
              <span>{inventory?.lowUnitThreshold?.toFixed(2) || '0.00'}</span>
            </div>
            <div className={styles.infoItem}>
              <label>Auto-buy threshold:</label>
              <span>
                {inventory?.autoBuyThresholdAmount?.toLocaleString() || '0'}
              </span>
            </div>
            <div className={styles.infoItem}>
              <label>Trading Status:</label>
              <span
                className={
                  inventory?.enabledForTrading
                    ? styles.enabled
                    : styles.disabled
                }>
                {inventory?.enabledForTrading ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            {inventory?.healthPercentage !== undefined && (
              <div className={styles.infoItem}>
                <label>Health Percentage:</label>
                <span
                  className={
                    inventory?.healthPercentage < 50
                      ? styles.critical
                      : styles.healthy
                  }>
                  {inventory?.healthPercentage}%
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const units = value =>
  new Intl.NumberFormat('en-US', {maximumFractionDigits: 20}).format(
    Number(value) || 0,
  );

const money = value =>
  new Intl.NumberFormat('en-US', {
    minimumFractionDigits: Number.isInteger(Number(value)) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

export const InventoryMovementModal = ({
  form,
  onClose,
  refetch,
  currency,
  instrumentId,
  instrumentKey,
  position,
}) => {
  const {key} = useParams();
  const hasPosition = !!position;
  const inventoryKey = instrumentKey ?? defaultInstrumentKey(key);
  const trackBroker = ['BOND', 'MUTUAL_FUND'].includes(inventoryKey);
  const [requestKey, setRequestKey] = useState(uuid);
  const {data: brokerData, loading: brokersLoading, error: brokersError} = useQuery(GET_ALL_BROKERS, {skip: !trackBroker});
  const eligibleBrokers = (brokerData?.getBrokers?.data || []).filter(broker =>
    broker.enabled && !broker.master && broker.currency === currency &&
    (inventoryKey === 'BOND' ? ['BOND', 'FIXED_INCOMES'] : ['MUTUAL_FUND', 'FUNDS']).includes(broker.assetClass));


  /**
   * The movement is not booked straight off the form.
   *
   * It writes stock: an outbound movement takes units out of a live position
   * and there is no undo in this screen, so a mistyped quantity used to land in
   * the ledger on a single click. Holding the values here turns the form into a
   * draft and gives the operator the one thing the form could not show them —
   * what the position will read afterwards.
   */
  const [pending, setPending] = useState(null);

  const [createInventoryMovement, {loading}] = useMutation(
    CREATE_INVENTORY_MOVEMENT,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const handleSubmit = values => {
    createInventoryMovement({
      variables: {
        input: {...values, ...(trackBroker ? {requestKey, effectiveAt: values.effectiveAt ? `${values.effectiveAt}T00:00:00.000Z` : undefined} : {})},
        // `createInventoryMovementId` used to be passed here too. The mutation
        // declares only `input`, `instrumentId` and `instrumentKey`, so it was
        // an undeclared extra that the server discarded — and threading it
        // implied a position id was needed, which is what kept this action
        // disabled on instruments that had no inventory yet.
        instrumentId: instrumentId,
        // Was a binary FIXED_INCOMES ? BOND : MUTUAL_FUND, so a movement on
        // an IPO was booked against the wrong instrument type. The position
        // itself carries the key it belongs to.
        instrumentKey: instrumentKey ?? defaultInstrumentKey(key),
      },
    })
      .then(({data: {createInventoryMovement}}) => {
        if (createInventoryMovement.__typename === 'Error') {
          toast.error(createInventoryMovement.message);
        } else {
          toast.success(createInventoryMovement.message);
          setRequestKey(uuid());
          setPending(null);
          onClose();
          refetch();
        }
      })
      .catch(error => toast.error(error.message));
  };

  const available = Number(position?.availableUnits) || 0;

  if (pending) {
    const isOutbound = pending.direction === 'OUTBOUND';
    const moved = Number(pending.units) || 0;
    const after = isOutbound ? available - moved : available + moved;
    // An outbound movement larger than the position holds cannot be booked,
    // and saying so here beats a server error after the fact.
    const overdrawn = isOutbound && moved > available;

    return (
      <div className={styles.inventoryMovementModal}>
        <h2>Confirm this movement</h2>
        <p className={styles.movementIntro}>
          {isOutbound
            ? 'This removes units from the position.'
            : hasPosition
            ? 'This adds units to the position.'
            : 'This opens a new inventory position for the instrument.'}
        </p>

        <dl className={styles.confirmRows}>
          <div>
            <dt>Direction</dt>
            <dd>{isOutbound ? 'Remove units' : 'Add units'}</dd>
          </div>
          <div>
            <dt>Units</dt>
            <dd>{units(pending.units)}</dd>
          </div>
          <div>
            <dt>Price per unit</dt>
            <dd>
              {currency} {money(pending.pricePerUnit)}
            </dd>
          </div>
          <div>
            <dt>Total value</dt>
            <dd>
              {currency} {money(moved * (Number(pending.pricePerUnit) || 0))}
            </dd>
          </div>
          {trackBroker && <>
            <div><dt>Source</dt><dd>{pending.movementKind === 'BROKER_TOP_UP' ? 'Broker purchase' : 'Inventory adjustment'}</dd></div>
            {pending.brokerId && <div><dt>Broker</dt><dd>{eligibleBrokers.find(b => b.id === pending.brokerId)?.name}</dd></div>}
            {pending.brokerFees != null && <div><dt>Actual broker fee</dt><dd>{currency} {money(pending.brokerFees)}</dd></div>}
            <div><dt>Transaction date</dt><dd>{pending.effectiveAt || new Date().toISOString().slice(0, 10)}</dd></div>
          </>}
          {hasPosition && (
            <div>
              <dt>Available units</dt>
              <dd>
                {units(available)} → <strong>{units(after)}</strong>
              </dd>
            </div>
          )}
        </dl>

        {overdrawn && (
          <p className={styles.confirmWarning}>
            This removes {units(moved)} units but only {units(available)} are
            available. Reduce the quantity before booking it.
          </p>
        )}

        <div className={styles.confirmActions}>
          <Button
            type="secondary"
            label="Back"
            onClick={() => setPending(null)}
          />
          <Button
            label={loading ? 'Booking…' : 'Confirm movement'}
            disabled={loading || overdrawn}
            onClick={() => handleSubmit(pending)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.inventoryMovementModal}>
      <h2>{hasPosition ? 'Create Inventory Movement' : 'Open Inventory'}</h2>
      {!hasPosition && (
        <p className={styles.movementIntro}>
          This instrument has no inventory yet. Booking the first inbound
          movement opens a position, after which you can configure its settings.
        </p>
      )}
      <Form
        className={styles.form}
        layout="vertical"
        form={form}
        onFinish={values => setPending({...values, ...(trackBroker && (values.direction === 'OUTBOUND' || values.movementKind === 'ADJUSTMENT') ? {movementKind: 'ADJUSTMENT', brokerId: undefined, brokerFees: undefined} : {})})}>
        <Form.Item
          className={styles.fieldWidth}
          name="description"
          label="Description">
          <Input.TextArea rows={4} />
        </Form.Item>
        <Form.Item
          rules={[{required: true, message: 'Please select direction'}]}
          className={styles.fieldWidth}
          name="direction"
          label="Direction">
          <Select
            options={[
              {label: 'Add', value: 'INBOUND'},
              {label: 'Remove', value: 'OUTBOUND'},
            ]}
          />
        </Form.Item>
        {trackBroker && <Form.Item noStyle shouldUpdate>
          {({getFieldValue}) => getFieldValue('direction') === 'INBOUND' && <>
            <Form.Item name="movementKind" label="Source" initialValue="BROKER_TOP_UP" preserve={false} rules={[{required: true}]}>
              <Select options={[{label: 'Broker purchase', value: 'BROKER_TOP_UP'}, {label: 'Inventory adjustment (broker unknown)', value: 'ADJUSTMENT'}]} />
            </Form.Item>
            <Form.Item noStyle shouldUpdate>
              {({getFieldValue: getValue}) => getValue('movementKind') !== 'ADJUSTMENT' && <>
                <Form.Item name="brokerId" label="Supplying broker" preserve={false} rules={[{required: true, message: 'Select the broker supplying these units'}]}>
                  <Select loading={brokersLoading} showSearch optionFilterProp="label" options={eligibleBrokers.map(broker => ({label: broker.name, value: broker.id}))} />
                </Form.Item>
                {brokersError && <p role="alert">Could not load brokers. Please retry.</p>}
                {!brokersLoading && !brokersError && !eligibleBrokers.length && <p>No enabled broker is configured for this asset class and currency. Add the provider in broker settings first.</p>}
                <Form.Item name="brokerFees" label="Actual broker fee" preserve={false} rules={[{required: true, message: 'Enter the actual fee, including zero if none was charged'}]}>
                  <InputNumber min={0} addonAfter={currency} />
                </Form.Item>
              </>}
            </Form.Item>
          </>}
        </Form.Item>}
        {trackBroker && <Form.Item name="effectiveAt" label="Transaction date" tooltip="Defaults to today. Earlier dates require rerunning the report for that day." rules={[{validator: (_, value) => !value || (/^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value && value <= new Date().toISOString().slice(0, 10)) ? Promise.resolve() : Promise.reject(new Error('Enter a valid date no later than today'))}]}>
          <Input type="date" max={new Date().toISOString().slice(0, 10)} />
        </Form.Item>}
        <Form.Item
          rules={[{required: true, message: 'Please enter units'}]}
          className={styles.fieldWidth}
          name="units"
          label="Units">
          <InputNumber
            addonAfter="UNITS"
            min={0.000000000001}
            className="input-number"
            parser={value => value.replace(/\$\s?|(,*)/g, '')}
          />
        </Form.Item>
        <Form.Item
          rules={[{required: true, message: 'Please enter price per unit'}]}
          className={styles.fieldWidth}
          name="pricePerUnit"
          label="Price Per Unit">
          <InputNumber
            addonAfter={currency}
            min={1}
            className="input-number"
            parser={value => value.replace(/\$\s?|(,*)/g, '')}
          />
        </Form.Item>
        <SubmitButton disabled={loading} label="Review" />
      </Form>
    </div>
  );
};
